import { useCallback, useEffect, useState } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Paper, Box, Typography, TextField, Button, Chip, Tabs, Tab, IconButton,
  FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs, { Dayjs } from "dayjs";
import * as Sentry from "@sentry/react";
import PageMeta from "../../components/common/PageMeta";
import PageContainer from "../../components/common/PageContainer";
import PageHeader from "../../components/common/PageHeader";
import { useApi } from "../../hooks/useApi";
import { formatCurrency } from "../../utils/formatCurrency";
import type { CreditSummary, CreditSummaryBillingProfile, DismissResult, IssuedBatch, IssueResult } from "../../types/billingCredits";

type TabKey = "review" | "history";

const HISTORY_MONTHS = 3;

const tabsSx = {
  mb: 3,
  borderBottom: "1px solid #e4e7ec",
  "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
  "& .MuiTabs-indicator": { backgroundColor: "#465fff" },
};

const money = (cents: number | null | undefined, currency: string | null) =>
  cents == null ? "—" : currency ? formatCurrency(cents, currency) : (cents / 100).toFixed(2);

const fmtDate = (d: string | null | undefined) => (d ? dayjs(d).format("YYYY-MM-DD") : "—");

const monthLabel = (month: string) => dayjs(`${month}-01`).format("MMMM YYYY");

export default function BillingCredits() {
  const { get, post } = useApi();

  const [tab, setTab] = useState<TabKey>("review");
  const [month, setMonth] = useState<Dayjs>(dayjs().subtract(1, "month").startOf("month"));
  const [fetching, setFetching] = useState(false);

  const [summary, setSummary] = useState<CreditSummary | null>(null);
  const [fetchedMonth, setFetchedMonth] = useState<string | null>(null);
  const [amounts, setAmounts] = useState<Map<number, string>>(new Map());
  const [reviewPage, setReviewPage] = useState(0);
  const [reviewRpp, setReviewRpp] = useState(25);

  const [history, setHistory] = useState<IssuedBatch[]>([]);
  const [historyAll, setHistoryAll] = useState(false);
  const [historyMonth, setHistoryMonth] = useState<Dayjs | null>(null);
  const [showDismissed, setShowDismissed] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyRpp, setHistoryRpp] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [issueTarget, setIssueTarget] = useState<CreditSummaryBillingProfile | null>(null);
  const [issueReason, setIssueReason] = useState("");
  const [issuing, setIssuing] = useState(false);

  const [dismissTarget, setDismissTarget] = useState<CreditSummaryBillingProfile | "all" | null>(null);
  const [dismissReason, setDismissReason] = useState("");
  const [dismissing, setDismissing] = useState(false);

  const [toast, setToast] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    const params = new URLSearchParams();
    if (historyMonth) {
      params.set("month", historyMonth.format("YYYY-MM"));
    } else if (!historyAll) {
      params.set("months", String(HISTORY_MONTHS));
    }
    if (showDismissed) params.set("includeDismissed", "true");
    const qs = params.toString();
    setHistory(await get<IssuedBatch[]>(`/api/billing-credits/history${qs ? `?${qs}` : ""}`, { slowApi: true }));
  }, [get, historyMonth, historyAll, showDismissed]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await loadHistory();
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load history");
        Sentry.captureException(err);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadHistory]);

  const runFetch = async () => {
    setFetching(true);
    setError(null);
    try {
      const m = month.format("YYYY-MM");
      const res = await post<CreditSummary>("/api/billing-credits/fetch", { month: m }, { slowApi: true });
      setSummary(res);
      setReviewPage(0);
      setFetchedMonth(m);
      setAmounts(new Map(res.profiles.map((p) => [p.billingProfileId, p.amountCents == null ? "" : (p.amountCents / 100).toFixed(2)])));
      setTab("review");
      const hidden = res.handled.issuedCount + res.handled.dismissedCount;
      setToast(`Fetched ${res.profiles.length} billing profiles (${res.unmapped.alertCount} unmapped alerts${hidden ? `, ${hidden} already handled` : ""})`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Fetch failed");
      Sentry.captureException(err);
    } finally {
      setFetching(false);
    }
  };

  const setAmount = (billingProfileId: number, value: string) =>
    setAmounts((prev) => new Map(prev).set(billingProfileId, value));

  const issueCents = issueTarget ? Math.round(parseFloat(amounts.get(issueTarget.billingProfileId) ?? "0") * 100) : 0;
  const reasonRequired = issueTarget != null && issueCents !== issueTarget.amountCents;

  const doIssue = async () => {
    if (!issueTarget || !fetchedMonth) return;
    if (!(issueCents > 0) || (reasonRequired && !issueReason.trim())) return;
    setError(null);
    setIssuing(true);
    try {
      const res = await post<IssueResult>("/api/billing-credits/issue", {
        billingProfileId: issueTarget.billingProfileId,
        month: fetchedMonth,
        amountCents: issueCents,
        alertCount: issueTarget.alertCount,
        reason: issueReason.trim() || undefined,
      }, { slowApi: true });
      setToast(`Issued ${money(res.amountCents, res.currency)} to ${issueTarget.billingProfileName}`);
      setSummary((s) => (s ? {
        ...s,
        profiles: s.profiles.filter((p) => p.billingProfileId !== issueTarget.billingProfileId),
        handled: { ...s.handled, issuedCount: s.handled.issuedCount + 1 },
      } : s));
      setIssueTarget(null);
      await loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Issue failed");
      Sentry.captureException(err);
    } finally {
      setIssuing(false);
    }
  };

  const doDismiss = async () => {
    if (!dismissTarget || !fetchedMonth || !summary) return;
    const targets = dismissTarget === "all" ? summary.profiles : [dismissTarget];
    if (targets.length === 0) return;
    setError(null);
    setDismissing(true);
    try {
      // Bulk schema caps at 100 profiles per request — chunk dismiss-all.
      const results: DismissResult[] = [];
      for (let i = 0; i < targets.length; i += 100) {
        results.push(await post<DismissResult>("/api/billing-credits/dismiss", {
          month: fetchedMonth,
          profiles: targets.slice(i, i + 100).map((p) => ({
            billingProfileId: p.billingProfileId,
            alertCount: p.alertCount,
            ...(p.amountCents != null && { amountCents: p.amountCents }),
          })),
          reason: dismissReason.trim() || undefined,
        }, { slowApi: true }));
      }
      const res: DismissResult = {
        dismissed: results.flatMap((r) => r.dismissed),
        skipped: results.flatMap((r) => r.skipped),
      };
      const removed = new Set([...res.dismissed, ...res.skipped]);
      setSummary((s) => (s ? {
        ...s,
        profiles: s.profiles.filter((p) => !removed.has(p.billingProfileId)),
        handled: { ...s.handled, dismissedCount: s.handled.dismissedCount + res.dismissed.length },
      } : s));
      setDismissTarget(null);
      setDismissReason("");
      setToast(`Dismissed ${res.dismissed.length} billing profile${res.dismissed.length === 1 ? "" : "s"}${res.skipped.length ? ` (${res.skipped.length} already handled)` : ""}`);
      await loadHistory();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Dismiss failed");
      Sentry.captureException(err);
    } finally {
      setDismissing(false);
    }
  };

  const hiddenCount = summary ? summary.handled.issuedCount + summary.handled.dismissedCount : 0;

  const reviewProfiles = summary?.profiles ?? [];
  const reviewPageClamped = Math.min(reviewPage, Math.max(0, Math.ceil(reviewProfiles.length / reviewRpp) - 1));
  const pagedProfiles = reviewProfiles.slice(reviewPageClamped * reviewRpp, (reviewPageClamped + 1) * reviewRpp);
  const historyPageClamped = Math.min(historyPage, Math.max(0, Math.ceil(history.length / historyRpp) - 1));
  const pagedHistory = history.slice(historyPageClamped * historyRpp, (historyPageClamped + 1) * historyRpp);

  return (
    <PageContainer>
      <PageMeta title="Chargemont | Billing Credits" description="Ethoca credited alerts → Stripe billing credits" />
      <PageHeader title="Billing Credits" />

      {error && (
        <Box className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg">Error: {error}</Box>
      )}

      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box display="flex" alignItems="center" gap={1} mb={3}>
          <IconButton size="small" onClick={() => setMonth((m) => m.subtract(1, "month"))} aria-label="previous month">
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
          <DatePicker
            label="Credited month"
            views={["year", "month"]}
            openTo="month"
            value={month}
            onChange={(v) => { if (v) setMonth(v.startOf("month")); }}
            maxDate={dayjs()}
            slotProps={{
              textField: {
                size: "small",
                helperText: month.isSame(dayjs(), "month") ? "Current month — data is still partial" : undefined,
              },
            }}
          />
          <IconButton size="small" onClick={() => setMonth((m) => m.add(1, "month"))}
            disabled={month.isSame(dayjs(), "month")} aria-label="next month">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
          <Button variant="contained" onClick={runFetch} disabled={fetching}
            startIcon={fetching ? <CircularProgress size={16} color="inherit" /> : undefined}>
            {fetching ? "Fetching…" : "Fetch"}
          </Button>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabsSx}>
          <Tab label={`Review${summary ? ` (${summary.profiles.length})` : ""}`} value="review" />
          <Tab label={`History${history.length ? ` (${history.length})` : ""}`} value="history" />
        </Tabs>

        {loading ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : tab === "review" ? (
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            {summary && (hiddenCount > 0 || summary.profiles.length > 0) && (
              <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {hiddenCount > 0
                    ? `${hiddenCount} billing profiles hidden — already handled for ${fetchedMonth ? monthLabel(fetchedMonth) : ""} (${summary.handled.issuedCount} issued, ${summary.handled.dismissedCount} dismissed)`
                    : `Nothing handled yet for ${fetchedMonth ? monthLabel(fetchedMonth) : ""}`}
                </Typography>
                {summary.profiles.length > 0 && (
                  <Button size="small" color="inherit" onClick={() => { setError(null); setDismissTarget("all"); setDismissReason(""); }}>
                    Dismiss all
                  </Button>
                )}
              </Box>
            )}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Billing profile</TableCell>
                    <TableCell align="right">Alerts</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedProfiles.map((p) => {
                    const amount = amounts.get(p.billingProfileId) ?? "";
                    const cents = Math.round(parseFloat(amount || "0") * 100);
                    return (
                      <TableRow key={p.billingProfileId} hover>
                        <TableCell>{p.companyName}</TableCell>
                        <TableCell>
                          {p.billingProfileName}
                          {p.amountCents == null && <Chip size="small" color="warning" label="fee unresolved" sx={{ ml: 1 }} />}
                        </TableCell>
                        <TableCell align="right">{p.alertCount}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
                            {p.unitFeeCents != null && (
                              <Typography variant="caption" color="text.secondary">
                                {money(p.unitFeeCents, p.currency)} × {p.alertCount}
                              </Typography>
                            )}
                            <TextField size="small" value={amount} onChange={(e) => setAmount(p.billingProfileId, e.target.value)}
                              slotProps={{ input: { startAdornment: <Typography variant="body2" sx={{ mr: 0.5, color: "text.secondary" }}>{p.currency?.toUpperCase() ?? ""}</Typography> } }}
                              sx={{ width: 130 }} />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Button size="small" color="inherit" sx={{ mr: 1 }}
                            onClick={() => { setError(null); setDismissTarget(p); setDismissReason(""); }}>Dismiss</Button>
                          <Button variant="contained" size="small" disabled={!(cents > 0)}
                            onClick={() => { setError(null); setIssueTarget(p); setIssueReason(""); }}>Issue</Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {summary && summary.profiles.length === 0 && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>No unhandled credits for this month.</TableCell></TableRow>
                  )}
                  {!summary && (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>Pick a month and fetch to load creditable alerts.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {reviewProfiles.length > 0 && (
              <TablePagination component="div" count={reviewProfiles.length} page={reviewPageClamped}
                rowsPerPage={reviewRpp} rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, p) => setReviewPage(p)}
                onRowsPerPageChange={(e) => { setReviewRpp(Number(e.target.value)); setReviewPage(0); }} />
            )}
            {summary && summary.unmapped.alertCount > 0 && (
              <Box sx={{ p: 2, color: "text.secondary" }}>
                <Typography variant="body2">
                  {summary.unmapped.alertCount} credited alert(s) were left out — no company mapping, or the mapped company has more than one billing profile.
                </Typography>
              </Box>
            )}
          </Paper>
        ) : (
          <Paper sx={{ width: "100%", overflow: "hidden" }}>
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
              <DatePicker
                label="Month"
                views={["year", "month"]}
                openTo="month"
                value={historyMonth}
                onChange={(v) => { setHistoryMonth(v ? v.startOf("month") : null); setHistoryPage(0); }}
                slotProps={{ textField: { size: "small" }, field: { clearable: true } }}
              />
              <FormControlLabel
                control={<Switch size="small" checked={showDismissed} onChange={(e) => setShowDismissed(e.target.checked)} />}
                label={<Typography variant="body2">Show dismissed</Typography>}
              />
              {!historyMonth && !historyAll && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
                  Showing the last {HISTORY_MONTHS} months.{" "}
                  <Button size="small" onClick={() => { setHistoryAll(true); setHistoryPage(0); }}>Show all</Button>
                </Typography>
              )}
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Billing profile</TableCell>
                    <TableCell>Window</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Alerts</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedHistory.map((b) => (
                    <TableRow key={b.billing_credit_batch_id} hover>
                      <TableCell>{b.company_name}</TableCell>
                      <TableCell>{b.billing_profile_name}</TableCell>
                      <TableCell>{fmtDate(b.credited_from)} – {fmtDate(b.credited_to)}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined"
                          color={b.status === "issued" ? "success" : "default"} label={b.status} />
                      </TableCell>
                      <TableCell align="right">{b.alert_count}</TableCell>
                      <TableCell align="right">{money(b.amount_cents, b.currency)}</TableCell>
                      <TableCell>{b.reason || "—"}</TableCell>
                      <TableCell>{fmtDate(b.reviewed_at)}</TableCell>
                      <TableCell>{[b.reviewed_by_first_name, b.reviewed_by_last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                    </TableRow>
                  ))}
                  {history.length === 0 && (
                    <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: "text.secondary" }}>No credits recorded yet.</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {history.length > 0 && (
              <TablePagination component="div" count={history.length} page={historyPageClamped}
                rowsPerPage={historyRpp} rowsPerPageOptions={[25, 50, 100]}
                onPageChange={(_, p) => setHistoryPage(p)}
                onRowsPerPageChange={(e) => { setHistoryRpp(Number(e.target.value)); setHistoryPage(0); }} />
            )}
          </Paper>
        )}
      </LocalizationProvider>

      <Dialog open={!!issueTarget} onClose={() => { if (!issuing) setIssueTarget(null); }} fullWidth maxWidth="xs">
        <DialogTitle>Issue billing credit</DialogTitle>
        <DialogContent>
          {issueTarget && (
            <Typography sx={{ mb: 2 }}>
              Issue <b>{money(issueCents, issueTarget.currency)}</b>{" "}
              to <b>{issueTarget.billingProfileName}</b> ({issueTarget.alertCount} alerts, {fetchedMonth ? monthLabel(fetchedMonth) : ""})?
            </Typography>
          )}
          <TextField autoFocus fullWidth multiline minRows={2}
            label={reasonRequired ? "Reason (required — amount adjusted)" : "Reason (optional)"}
            value={issueReason} onChange={(e) => setIssueReason(e.target.value)} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssueTarget(null)} disabled={issuing}>Cancel</Button>
          <Button variant="contained" onClick={doIssue} disabled={issuing || (reasonRequired && !issueReason.trim())}
            startIcon={issuing ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Issue credit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!dismissTarget} onClose={() => { if (!dismissing) setDismissTarget(null); }} fullWidth maxWidth="xs">
        <DialogTitle>Dismiss without crediting</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {dismissTarget === "all"
              ? <>Mark all <b>{summary?.profiles.length ?? 0}</b> listed billing profiles as handled for <b>{fetchedMonth ? monthLabel(fetchedMonth) : ""}</b>? They will not be credited and will not reappear on future fetches.</>
              : dismissTarget
                ? <>Mark <b>{dismissTarget.billingProfileName}</b> as handled for <b>{fetchedMonth ? monthLabel(fetchedMonth) : ""}</b> without crediting? It will not reappear on future fetches.</>
                : null}
          </Typography>
          <TextField autoFocus fullWidth multiline minRows={2} label="Reason (optional)"
            placeholder="e.g. settled outside the portal"
            value={dismissReason} onChange={(e) => setDismissReason(e.target.value)} />
          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissTarget(null)} disabled={dismissing}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doDismiss} disabled={dismissing}
            startIcon={dismissing ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity="success" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </PageContainer>
  );
}
